import { isAtEndOfNode } from '@tiptap/core';

import {
  AlignRight,
  AlignLeft,
  PilcrowLeft,
  PilcrowRight,
  AlignCenter,
  AlignJustify,
} from 'lucide-vue-next';
import { ref } from 'vue';
import { getEditor, getEditorState, isEditorRTL } from './editorsMap';
import { get } from 'lodash';
import { getMarksBetween } from '@tiptap/vue-3';
import {isHTML} from "@amit/tiptap";

const defaultColors = [
  '#b0bd21',
  '#14365e',
  '#2badde',
  '#21739e',
  '#6db580',
  '#4d8879',
  '#637840',
];

export const textPalettes = [
  {
    colors: ['#C2F4CF', '#EBF391', '#D6F2B5', '#49DF94', '#CCDC18', '#8FDC38'],
  },
  {
    colors: ['#FADCB2', '#FDD3E6', '#DED4F7', '#F4BE34', '#F075AC', '#9774E7'],
  },
  {
    colors: ['#D63434', '#5192F6', '#CCDC18', '#FFFFFF', '#1E232A', '#67768E'],
  },
  {
    colors: defaultColors,
  },
];
export const highlightPalettes = [
  {
    colors: [
      '#1E232A',
      '#495465',
      '#FFFFFF',
      '#0FA9FF',
      '#52C1FF',
      '#0093E5',
      '#F0920F',
      '#B82626',
      '#3F9F44',
    ],
  },
  {
    colors: ['#49DF94', '#8FDC38', '#26D9D9', '#9774E7', '#F075AC', '#5192F6'],
  },
  {
    colors: defaultColors,
  },
];

export const backgroundPalettes = [
  {
    colors: [
      '#E5FAEA',
      '#F7FAD1',
      '#ECF9DC',
      '#C2F4CF',
      '#EBF391',
      '#D6F2B5',
      '#49DF94',
      '#CCDC18',
      '#8FDC38',
    ],
  },
  {
    colors: [
      '#FDF4D8',
      '#FDEDF4',
      '#EBE5FA',
      '#FADCB2',
      '#FDD3E6',
      '#DED4F7',
      '#F4BE34',
      '#F075AC',
      '#9774E7',
    ],
  },
  {
    colors: defaultColors,
  },
];

export const fontSizeOptions = [
  {
    text: '12',
    actionMethod: 'setFontSize',
    attributes: '12px',
    applyTo: ['body'],
  },
  {
    text: '14',
    actionMethod: 'setFontSize',
    attributes: '14px',
    applyTo: ['body'],
  },

  {
    text: '16',
    actionMethod: 'setFontSize',
    attributes: '16px',
    applyTo: ['body', 'h1', 'h3'],
  },
  {
    text: '18',
    actionMethod: 'setFontSize',
    attributes: '18px',
    applyTo: ['body', 'handwriting', 'h1', 'h3'],
  },
  {
    text: '20',
    actionMethod: 'setFontSize',
    attributes: '20px',
    applyTo: ['body', 'handwriting', 'h1', 'h3'],
  },
  {
    text: '22',
    actionMethod: 'setFontSize',
    attributes: '22px',
    applyTo: ['body', 'handwriting', 'h1', 'h3'],
  },
  {
    text: '24',
    actionMethod: 'setFontSize',
    attributes: '24px',
    applyTo: ['body', 'handwriting', 'h1', 'h3'],
  },
  {
    text: '26',
    actionMethod: 'setFontSize',
    attributes: '26px',
    applyTo: ['handwriting', 'h1', 'h3', 'display_md'],
  },
  {
    text: '28',
    actionMethod: 'setFontSize',
    attributes: '28px',
    applyTo: ['handwriting', 'display_md'],
  },
  {
    text: '30',
    actionMethod: 'setFontSize',
    attributes: '30px',
    applyTo: ['handwriting', 'display_md'],
  },
  {
    text: '32',
    actionMethod: 'setFontSize',
    attributes: '32px',
    applyTo: ['handwriting', 'display_md'],
  },
  {
    text: '34',
    actionMethod: 'setFontSize',
    attributes: '34px',
    applyTo: ['display_md'],
  },
  {
    text: '36',
    actionMethod: 'setFontSize',
    attributes: '36px',
    applyTo: ['display_md'],
  },
];

export const alignmentsOptions = [
  {
    icon: AlignRight,
    actionMethod: 'setTextAlign',
    attributes: 'right',
  },
  {
    icon: AlignCenter,
    actionMethod: 'setTextAlign',
    attributes: 'center',
  },
  {
    icon: AlignLeft,
    actionMethod: 'setTextAlign',
    attributes: 'left',
  },
  {
    icon: AlignJustify,
    actionMethod: 'setTextAlign',
    attributes: 'justify',
  },
];
export const directionOptions = [
  {
    icon: PilcrowLeft,
    actionMethod: 'setDirection',
    attributes: 'rtl',
  },
  {
    icon: PilcrowRight,
    actionMethod: 'setDirection',
    attributes: 'ltr',
  },
];
export const spacingOptions = [
  {
    text: 'ריווח בין שורות',
    disabled: true,
    classes: 'font-simplerBold',
  },
  {
    text: '1.15',
    attributes: 1.15,
    type: 'line',
    default: true,
  },
  {
    text: '1.5',
    attributes: 1.5,
    type: 'line',
  },
  {
    text: 'כפול',
    attributes: 2,
    type: 'line',
  },
  {
    text: 'ריווח בין פסקאות',
    disabled: true,
    classes: 'font-simplerBold',
  },
  {
    text: '20',
    attributes: { class: 'mb-4' },
    type: 'paragraph',
    default: true,
  },
  {
    text: '25',
    attributes: { class: 'mb-6' },
    type: 'paragraph',
  },
  {
    text: '30',
    attributes: { class: 'mb-8' },
    type: 'paragraph',
  },
];
export const typographyOptions = [
  {
    text: 'טקסט ענק',
    classes: 'display_md',
    actionMethod: 'setParagraph',
    attributes: { class: 'display_md', fontSize: '32px' },
  },
  {
    text: 'כותרת עמוד',
    classes: 'title_xl',
    actionMethod: 'setHeading',
    attributes: { level: 1, class: 'h1', fontSize: '24px' },
  },
  {
    text: 'כותרת משנית',
    classes: 'title_md',
    actionMethod: 'setHeading',
    attributes: { level: 3, class: 'h3', fontSize: '20px' },
  },
  {
    text: 'טקסט פסקה',
    classes: 'body',
    actionMethod: 'setParagraph',
    attributes: { class: 'body', fontSize: '18px' },
  },
  {
    text: 'כתב יד',
    classes: 'handwriting text-xl leading-[0.5] mb-1',
    actionMethod: 'setParagraph',
    attributes: { class: 'handwriting', fontSize: '26px' },
  },
];

export const getCleanClass = (node, option) => {
  const styles = ['display_md', 'h1', 'h3', 'body', 'handwriting'];
  const classes = `${node.attrs.class ?? ''} ${option?.attributes?.class ?? ''}`;
  let classesArray = classes.split(/\s+/);
  let otherClasses = classesArray.filter(
    cls => !cls.startsWith('mb-') && !styles.includes(cls),
  );
  let lastMbClass = classesArray.filter(cls => cls.startsWith('mb-')).pop();

  if (lastMbClass) {
    otherClasses.push(lastMbClass);
  } else {
    otherClasses.push('mb-4');
  }

  const lastStyleClass = classesArray.filter(cls => styles.includes(cls)).pop();
  if (lastStyleClass) {
    otherClasses.push(lastStyleClass);
  }

  return otherClasses.join(' ').trim();
};

export const toggleList = (
  { commands, chain, state, dispatch },
  listType,
  options,
) => {
  const { tr } = state;
  const { from, to } = tr.selection;
  const changes = [];
  const firstNode = tr.doc.nodeAt(0);
  tr.doc.nodesBetween(from, to, (node, pos, parent) => {
    if (parent === tr.doc) {
      //@ts-ignore
      changes.push({
        pos,
        type: node.type,
        attrs: { ...node.attrs, indent: 0 },
      });
    }
  });
  const firstListItemAttributes = firstNode
    ? {
        indent: firstNode.attrs.indent,
        textAlign: firstNode.attrs.textAlign,
      }
    : {};

  changes.forEach(change => {
    //@ts-ignore
    tr.setNodeMarkup(change.pos, change.type, change.attrs);
  });
  dispatch(tr);

  if (options.keepAttributes) {
    return chain()
      .toggleList(listType, options.itemTypeName, options.keepMarks)
      .updateAttributes(listType, {
        ...firstListItemAttributes,
      })
      .run();
  }
  return commands.toggleList(listType, options.itemTypeName, options.keepMarks);
};

export const getParentTopLevel = (
  $from: any,
): { topLevelPos: number; topLevelNode: any } => {
  let pos = $from.pos;
  let depth = $from.depth;

  // Traverse up to the top level node
  while (depth > 0) {
    pos = $from.before(depth);
    depth--;
  }
  const topLevelPos = pos;
  const topLevelNode = $from.doc.nodeAt(topLevelPos);

  return { topLevelPos, topLevelNode };
};

export const isFirstNode = $from => {
  if ($from.pos <= 1) return true;
  return false;
};

export const isLastNode = ($from, tr) => {
  const { nextNodePos } = getNextNode(tr, $from.pos);
  return nextNodePos < 0;
};

export const getNextNode = (
  tr,
  currentPos,
  depth = null,
  excludeTypes = ['tableHeader', 'tableRow', 'tableCell'],
) => {
  let parentPos = -1;
  let found = false;
  let nextNode;
  let nextNodePos = -1;
  let nFound = false;
  let currentNodedepth = 0;

  tr.doc.nodesBetween(0, tr.doc.content.size, (node, pos, parent, pPos) => {
    if (!excludeTypes.includes(node.type.name)) {
      if (!found && (currentPos == pos || currentPos - 1 === pos)) {
        parentPos = pPos;
        currentNodedepth = depth ?? tr.doc.resolve(currentPos).depth;
        found = true;
      }
      if (found && nFound && tr.doc.resolve(pos).depth <= currentNodedepth) {
        nextNode = node;
        nextNodePos = pos;
        found = false;
      }
      if (found) {
        nFound = true;
      }
    }
  });

  return { nextNode, nextNodePos, parentPos };
};

export const getPreviousNode = (tr, currentPos, depth, excludeTypes = []) => {
  let parentPos = -1;
  let found = false;
  let prevNode;
  let prevNodePos = -1;
  let currentNodedepth = 0;

  tr.doc.nodesBetween(0, tr.doc.content.size, (node, pos, parent, pPos) => {
    //@ts-ignore
    if (!excludeTypes.includes(node.type.name)) {
      if (currentPos == pos) {
        parentPos = pPos;
        found = true;
        currentNodedepth = depth ?? tr.doc.resolve(currentPos).depth;
      }
      if (!found && tr.doc.resolve(pos).depth == currentNodedepth) {
        prevNode = node;
        prevNodePos = pos;
      }
    }
  });
  return { prevNode, prevNodePos, parentPos };
};

export const isAtEndOfMarkOrNode = state => {
  const { $from } = state.selection;

  const isEndOfNode = isAtEndOfNode(state);

  // Check if the position is at the end of any active marks
  const activeMarks = $from.marks();
  const isAtEndOfMark = activeMarks.some(mark => {
    const markEnd = $from.nodeAfter
      ? $from.nodeAfter.marks.find(m => m.type === mark.type) === undefined
      : true;
    return markEnd;
  });

  return isEndOfNode || isAtEndOfMark;
};

export const hasNodeWithAttribute = (id, attributeName) => {
  const editor = getEditor(id).value;
  if (!editor) return;
  const state = editor.state;
  const { from } = state.selection;
  let found = false;
  const node = state.doc.resolve(from).node(1);
  if (node?.attrs[attributeName]) {
    return true; // stop traversing once we find a match
  }
  return found;
};

export const isSelectionEmpty = ref(false);
export const configureEditorStyle = id => {
  if (!getEditor(id)) return;
  const options = { highlightColor: '#9dc1fb', textColor: 'white' };
  const { empty } = getEditor(id)?.value?.state.selection;
  isSelectionEmpty.value = empty;

  if (isSelectionEmpty.value) return;
  console.log('getEditor', getEditor(id));
  getEditor(id)
    .value?.chain()
    .setHighlight({ color: options.highlightColor })
    .setColor(options.textColor)
    .run();
};
export const undoEditorStyle = id => {
  if (!getEditor(id)) return;
  if (isSelectionEmpty.value) return;
  console.log(getEditor(id).value.chain());
  getEditor(id).value?.chain()?.undo()?.run();
};

export const isEmptyElement = (element: Element): boolean => {
  const normalizeText = (text: string): string => {
    // Remove zero-width spaces and trim
    return text.replace(/[\u200B]/g, '').trim();
  };

  const textContent = normalizeText(element.textContent ?? '');

  if (textContent.length === 0) {
    return true;
  }

  const katexElements = element.querySelectorAll('[data-katex]');
  let allKatexEmpty = true;

  for (const katexEl of Array.from(katexElements)) {
    const katexText = normalizeText(katexEl.textContent ?? '');
    if (katexText.length > 0 && katexText !== '$$') {
      allKatexEmpty = false;
      break;
    }
  }

  const nonKatexChildren = Array.from(element.children).filter(
      (child: Element) => !child.hasAttribute('data-katex')
  );
  return !textContent ||  textContent === '$$' && (nonKatexChildren.length === 0 && allKatexEmpty );
};

export const isTextEmpty = (htmlString: string): boolean => {
  if (!htmlString) return true;
  if(!isHTML(htmlString)) return !htmlString.length;

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const elements = doc.body.querySelectorAll('*');

  for (const element of Array.from(elements)) {
    if (!isEmptyElement(element)) {
      return false;
    }
  }
  return true
};

export const getSelected = (id, type) => {
  switch (type) {
    case 'highlight':
    case 'textStyle':
      return get(getCurrentMark(id, type), 'mark.attrs.color', '');

    case 'lineHeight':
      return get(
        getCurrentMark(id, 'lineHeight'),
        `mark.attrs.${type}`,
        spacingOptions.find(option => option.default && option.type == 'line')
          ?.attributes,
      );
    case 'fontSize':
      return (
        get(getCurrentMark(id, 'textStyle'), `mark.attrs.${type}`, '') ||
        getCurrentNodeAttr(id, type)
      );

    case 'backgroundColor':
      return getCurrentNodeAttr(id, 'color');

    case 'tableCellBackgroundColor':
      return getCurrentNodeAttr(id, 'backgroundColor', [
        //@ts-ignore
        'tableCell',
        //@ts-ignore
        'tableHeader',
      ]);

    case 'tableCellBorderColor':
      return getCurrentNodeAttr(id, 'borderColor', [
        //@ts-ignore
        'tableCell',
        //@ts-ignore
        'tableHeader',
      ]);
    default:
      return getCurrentNodeAttr(id, type);
  }
};

export const onSelectionUpdate = ({ editor }) => {
  const id = editor.appContext?.provides.blockId;
  const state = getEditorState(id);

  const { $from } = editor.state.selection;
  let node = $from.node($from.depth) ?? $from.parent;

  let foundActive = false;
  for (const option of typographyOptions) {
    const isActive =
      editor.isActive(
        option.actionMethod === 'setHeading' ? 'heading' : 'paragraph',
      ) && getCleanClass(node, null).includes(option.attributes.class);

    if (isActive) {
      state.activeTypo.value = option;
      foundActive = true;
      break;
    }
  }

  if (!foundActive) {
    state.activeTypo.value = null;
  }

  const nodeDir = node.attrs?.dir;
  const parentDir = $from.node($from.depth - 1)?.attrs?.dir;
  const isLtrDirection =
    nodeDir === 'ltr' || parentDir === 'ltr' || !isEditorRTL(id);

  state.directionRef.value = isLtrDirection ? PilcrowRight : PilcrowLeft;

  const align = getCurrentNodeAttr(id, 'textAlign');

  if (align === 'right') {
    state.alignmentRef.value = AlignRight;
  } else if (align === 'left') {
    state.alignmentRef.value = AlignLeft;
  } else if (align === 'center') {
    state.alignmentRef.value = AlignCenter;
  } else {
    const fallbackAlignment = isEditorRTL(id) ? AlignRight : AlignLeft;
    state.alignmentRef.value = fallbackAlignment;
  }

  state.typographyRef.value = state.activeTypo.value?.text ?? 'טקסט פסקה';
};

export const getCurrentMark = (id, mark) => {
  if (!getEditor(id)) return;
  const { from, to } = getEditor(id).value.state.selection;
  return getMarksBetween(from, to, getEditor(id).value.state.doc).find(
    selected => selected.mark.type.name == mark,
  );
};
export const getCurrentNodeAttr = (id, attr, nodeType = []) => {
  if (!getEditor(id)) return;
  let val;
  const { from, to } = getEditor(id).value?.state.selection ?? null;
  getEditor(id).value?.state.doc.nodesBetween(from, to, node => {
    if (node.attrs[attr]) {
      //@ts-ignore
      if (nodeType.length === 0 || nodeType.includes(node.type.name)) {
        val = node.attrs[attr];
        return node.attrs[attr];
      }
    }
  });
  return val;
};

export const textAreaExtensionSupport = {
  line_height: false,
  horizontal_rule: false,
  image: false,
  image_resize: false,
  hide_tooltip: false,
  background_color: false,
  hide_color: false,
  hide_link: false,
  block_quote: false,
  hide_table: false,
  hide_indent: false,
  hide_bullet_list: false,
  hide_ordered_list: false,
  hide_direction: false,
  hide_text_align: false,
  hide_font_size: false,
  hide_italic: false,
  hide_strike: false,
  hide_bold: false,
  hide_underline: false,
  hide_highlight: false,
  hide_text_type: false,
  hide_clear_text: false,
};

export const rebuildHtmlFromArray = items => {
  console.log('items', items);
  return (
    '<p class="mb-4 body">' +
    items
      .map(item => {
        return item
          .split(/(\$.*?\$)/) // split into text and katex segments
          .map(part => {
            if (/^\$.*?\$/.test(part)) {
              return `<span data-katex="true" editmode="false">${part.trim()}</span>`;
            } else {
              return part.trim();
            }
          })
          .join('');
      })
      .join(', ') +
    '</p>'
  );
};
export const extractTextAndKatexFromHtml = htmlString => {
  const container = document.createElement('div');
  container.innerHTML = htmlString;
  const result: string[] = [];
  function traverse(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent.trim();
      if (text) result.push(text);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      if (node.dataset.katex === 'true') {
        const katexText = node.textContent.trim();
        if (katexText) result.push(katexText);
      } else {
        node.childNodes.forEach(traverse);
      }
    }
  }
  container.childNodes.forEach(traverse);
  return result
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
};
